import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Modal } from "@/components";
import { useSnackBar } from "@/utils/useSnackbar";

import { useTriggerHubspotResyncMutation } from "@/pages/Private/redux/hubspot/hubspot.api";

const COOLDOWN_MS = 5000;

type RtkErrorShape = {
	status?: number;
	data?: { message?: string };
};

const getBackendMessage = (error: unknown): string | undefined => {
	if (typeof error === "object" && error !== null && "data" in error) {
		const data = (error as RtkErrorShape).data;

		if (data && typeof data.message === "string") {
			return data.message;
		}
	}

	return undefined;
};

export const HubspotResyncSection: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.hubspot.resync.${key}`);
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
	const [isCooldown, setIsCooldown] = useState<boolean>(false);

	const [triggerResync, { isLoading }] = useTriggerHubspotResyncMutation();

	useEffect(() => {
		if (!isCooldown) {
			return;
		}

		const timeout = window.setTimeout(() => setIsCooldown(false), COOLDOWN_MS);

		return () => window.clearTimeout(timeout);
	}, [isCooldown]);

	const handleOpenConfirm = () => setIsConfirmOpen(true);
	const handleCloseConfirm = () => setIsConfirmOpen(false);

	const handleConfirmResync = async () => {
		try {
			const response = await triggerResync().unwrap();

			setIsConfirmOpen(false);
			setIsCooldown(true);

			if (response.queuedCount === 0) {
				handleOpenSnackbar(ts("empty"));
			} else {
				handleOpenSnackbar(t("company.hubspot.resync.success", { count: response.queuedCount }));
			}
		} catch (error) {
			setIsConfirmOpen(false);
			const backendMessage = getBackendMessage(error);

			handleOpenSnackbar(backendMessage ?? ts("error"));
		}
	};

	const isButtonDisabled = isLoading || isCooldown;

	return (
		<>
			<div className="mt-6 pt-6 border-t border-border-light">
				<div className="text-sm font-medium text-text mb-2">{ts("title")}</div>
				<div className="text-sm text-text-meta mb-3">{ts("description")}</div>
				<div className="w-fit">
					<Button
						color={ButtonColor.ACTION}
						disabled={isButtonDisabled}
						isLoading={isLoading}
						size={ButtonSize.S}
						title={ts("button")}
						onClick={handleOpenConfirm}
					/>
				</div>
			</div>

			{isConfirmOpen && (
				<Modal
					handleClose={handleCloseConfirm}
					handleSave={handleConfirmResync}
					isLoading={isLoading}
					isOpened={isConfirmOpen}
					submitButtonText={ts("confirmCta")}
					title={ts("confirmTitle")}
				>
					<div className="text-sm text-text">{ts("confirmBody")}</div>
				</Modal>
			)}

			{renderMessageSnackbar()}
		</>
	);
};
